import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Calendly URLs mapping
export const CALENDLY_URLS = {
  clarityCall: "https://calendly.com/hello-innateperspectives/30min",
  preSessionOrientation: "https://calendly.com/hello-innateperspectives/pre-session-orientation",
  leadershipSession: "https://calendly.com/hello-innateperspectives/leadership-recalebration-session",
  fitConversation: "https://calendly.com/hello-innateperspectives/executive-capacity-recalibration-fit-conversation",
  focusedSession: "https://calendly.com/hello-innateperspectives/focused-stress-resolution",
  workshop: "https://calendly.com/hello-innateperspectives/stress-decision-architecture-workshop",
};

/**
 * Calendly Embed Modal Component
 * Opens Calendly in an embedded modal overlay
 */
export function CalendlyModal({ isOpen, onClose, calendlyUrl, title = "Schedule Your Session" }) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
      setIsLoading(true);
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-20"
      data-testid="calendly-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative w-full max-w-6xl h-[85vh] mx-4 bg-white rounded-lg shadow-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#ede8de]">
          <h3 className="font-serif text-xl text-[#1c1510]">{title}</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-[#ede8de] rounded-full"
            data-testid="calendly-modal-close"
          >
            <X size={20} className="text-[#1c1510]" />
          </Button>
        </div>
        
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10 mt-16">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-[#a07830] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#1c1510]">Loading scheduler...</p>
            </div>
          </div>
        )}
        
        {/* Calendly Embed */}
        <iframe
          src={`${calendlyUrl}?embed_type=Inline&hide_gdpr_banner=1`}
          className="w-full h-[calc(100%-57px)]"
          frameBorder="0"
          title="Schedule appointment"
          onLoad={() => setIsLoading(false)}
        />
      </div>
    </div>
  );
}

/**
 * Custom hook for managing Calendly modal state
 */
export function useCalendlyModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState(CALENDLY_URLS.clarityCall);
  const [title, setTitle] = useState("Schedule Your Session");

  const openModal = (url = CALENDLY_URLS.clarityCall, modalTitle = "Schedule Your Session") => {
    setCalendlyUrl(url);
    setTitle(modalTitle);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    calendlyUrl,
    title,
    openModal,
    closeModal,
  };
}

/**
 * Calendly Button Component
 * A ready-to-use button that opens Calendly in an embedded modal
 */
export function CalendlyButton({ 
  calendlyUrl = CALENDLY_URLS.clarityCall, 
  modalTitle = "Schedule Your Session",
  children = "Book a Clarity Call",
  className = "",
  variant = "default",
  ...props 
}) {
  const { isOpen, openModal, closeModal } = useCalendlyModal();

  return (
    <>
      <Button
        onClick={() => openModal(calendlyUrl, modalTitle)}
        className={className}
        variant={variant}
        {...props}
      >
        {children}
      </Button>
      <CalendlyModal
        isOpen={isOpen}
        onClose={closeModal}
        calendlyUrl={calendlyUrl}
        title={modalTitle}
      />
    </>
  );
}

export default CalendlyModal;
